import type {
  Product,
  ProductConnection,
} from "@shopify/hydrogen/storefront-api-types";
import { useCallback, useEffect, useMemo, useState } from "react";
import { flattenConnection } from "@shopify/hydrogen";
import { useUrl } from "@shopify/hydrogen";

interface UsePagination {
  collection: ProductConnection;
  itemsPerPage: number;
  url: string;
  articles?: any;
}

export function usePagination({
  itemsPerPage,
  collection,
  url,
  articles,
}: UsePagination) {
  const initialProducts = collection?.nodes || [];
  const [products, setProducts] = useState<Product[]>(initialProducts);
  const { hasNextPage, endCursor } = collection?.pageInfo ?? {};
  const [cursor, setCursor] = useState(endCursor ?? "");
  const [nextPage, setNextPage] = useState(hasNextPage);
  const [itemOffset, setItemOffset] = useState(0);

  useEffect(() => {
    setProducts(initialProducts);
    setCursor(endCursor ?? "");
    setNextPage(hasNextPage);
    setItemOffset(0);
  }, [collection, articles]);

  function paginateArray<T>(
    itemsPerPage: number,
    products: T[] = [],
    articles: T[] = []
  ): { products: T[][]; articles: T[][] } {
    if (products.length === 0 && articles.length === 0)
      return { products: [], articles: [] };

    const productPages: T[][] = [];
    const articlePages: T[][] = [];

    if (products.length > 0) {
      const productCombined = products.reduce((acc, value, index) => {
        const id = Math.floor(index / itemsPerPage);
        acc[id] = acc[id] ?? [];
        acc[id].push(value);
        return acc;
      }, [] as T[][]);

      productPages.push(...productCombined);
    }

    if (articles.length > 0) {
      const articleCombined = articles.reduce((acc, value, index) => {
        const id = Math.floor(index / itemsPerPage);
        acc[id] = acc[id] ?? [];
        acc[id].push(value);
        return acc;
      }, [] as T[][]);

      articlePages.push(...articleCombined);
    }

    return { products: productPages, articles: articlePages };
  }

  const currentItemsPaginate = paginateArray(
    itemsPerPage,
    products || [],
    articles || []
  );

  const currentItems = useMemo(() => {
    if (articles) {
      return {
        products: currentItemsPaginate.products[itemOffset] || [],
        blogs: currentItemsPaginate.articles[itemOffset] || [],
      };
    }
    return currentItemsPaginate.products[itemOffset] || [];
  }, [currentItemsPaginate, itemOffset, articles]);

  const fetchProducts = useCallback(async () => {
    const postUrl = new URL(
      (!!globalThis?.window?.location?.origin ?? "") + url
    );
    postUrl.searchParams.set("cursor", cursor);

    try {
      const { searchParams } = useUrl();
      const category = searchParams.get("category");
      const price = searchParams.get("price");
      if (category) postUrl.searchParams.set("category", category);
      if (price) postUrl.searchParams.set("price", price);
    } catch {}

    const response = await fetch(postUrl, {
      method: "POST",
    });
    const { data } = await response.json();

    // ProductGrid can paginate collection, products and search routes
    // @ts-ignore TODO: Fix types
    const newProducts: Product[] = flattenConnection<Product>(
      data?.collection?.products || data?.products || []
    );

    const { endCursor, hasNextPage } = data?.collection?.products?.pageInfo ||
      data?.products?.pageInfo || { endCursor: "", hasNextPage: false };

    setProducts([...products, ...newProducts]);
    setCursor(endCursor);
    setNextPage(hasNextPage);
  }, [cursor, url, products]);

  const productsLength = currentItemsPaginate.products.length;
  const articlesLength = currentItemsPaginate.articles.length;
  const maxLength = Math.max(productsLength, articlesLength);

  const pageCount = useMemo(
    () => (articles ? maxLength : productsLength),
    [articles, maxLength, productsLength]
  );

  function handlePage(event) {
    if (nextPage) {
      fetchProducts();
    }
    setItemOffset(event.selected);
  }

  return {
    models: {
      currentItems,
      pageCount,
      itemOffset,
    },
    operations: {
      handlePage,
    },
  };
}
